import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormFeedback,
  CFormInput,
  CFormLabel,
  CListGroup,
  CListGroupItem,
  CRow,
  CSpinner,
  CStepper,
} from "@coreui/react-pro";
import {
  useCustomerProfile,
  useUpdateCustomerProfile,
} from "../../api/hooks/useCustomerProfile";
import { useToast } from "../../context/useToast";
import type { ApiError } from "../../api/client";
import { useFailure } from "../../context/useFailure";
import { routePaths } from "../../routes/route-paths";

// CStepper's exposed imperative API. Mirrors the shape used by Signal
// Manager's onboarding wizard so the patterns stay aligned.
interface CStepperRef {
  next: () => void;
  prev: () => void;
  finish: () => void;
  reset: () => void;
}

interface FormData {
  firstName: string;
  lastName: string;
}

export function ProfileEditWizardCoreui() {
  const navigate = useNavigate();
  const { showSuccess, showWarning } = useToast();
  const { showFailure, clearFailure } = useFailure();
  const profile = useCustomerProfile();
  const updateProfile = useUpdateCustomerProfile();

  const stepperRef = useRef<CStepperRef>(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [identityValidated, setIdentityValidated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<FormData>({
    firstName: "",
    lastName: "",
  });

  useEffect(() => {
    if (profile.data) {
      setForm({
        firstName: profile.data.firstName ?? "",
        lastName: profile.data.lastName ?? "",
      });
    }
  }, [profile.data]);

  const identityValid =
    form.firstName.trim().length > 0 && form.lastName.trim().length > 0;

  const handleBack = () => {
    if (currentStep === 1) {
      navigate(routePaths.profile);
    } else {
      stepperRef.current?.prev();
    }
  };

  const handleIdentityNext = () => {
    if (!identityValid) {
      setIdentityValidated(true);
      return;
    }
    setIdentityValidated(false);
    stepperRef.current?.next();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    clearFailure();
    try {
      await updateProfile.mutateAsync({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
      });
      showSuccess("Profile saved.");
      navigate(routePaths.profile);
    } catch (err) {
      // The client interceptor has already made this safe to render — 4xx bodies are our own wording,
      // and anything 5xx is swapped for a generic line. Showing it beats "try again in a moment" for
      // a refusal that will never succeed on a retry.
      const apiError = err as ApiError;
      const message =
        err instanceof Error
          ? err.message
          : "We couldn't save your changes right now. Please try again in a moment.";

      // A refusal is something the member can act on and fits a toast; our own failure is not their
      // doing, and it carries a reference they may need to write down, so it stays on the page.
      if (apiError?.status !== undefined && apiError.status < 500) {
        showWarning(message);
      } else {
        showFailure(message, apiError?.traceId);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    {
      label: "Identity",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Identity</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              Used on your subscriptions and any future statements.
            </p>
            <CForm noValidate>
              <CRow className="mb-4">
                <CCol md={6}>
                  <CFormLabel htmlFor="firstName">First name</CFormLabel>
                  <CFormInput
                    id="firstName"
                    value={form.firstName}
                    onChange={(e) =>
                      setForm({ ...form, firstName: e.target.value })
                    }
                    invalid={
                      identityValidated && form.firstName.trim().length === 0
                    }
                    required
                  />
                  <CFormFeedback invalid>
                    First name is required.
                  </CFormFeedback>
                </CCol>
                <CCol md={6}>
                  <CFormLabel htmlFor="lastName">Last name</CFormLabel>
                  <CFormInput
                    id="lastName"
                    value={form.lastName}
                    onChange={(e) =>
                      setForm({ ...form, lastName: e.target.value })
                    }
                    invalid={
                      identityValidated && form.lastName.trim().length === 0
                    }
                    required
                  />
                  <CFormFeedback invalid>
                    Last name is required.
                  </CFormFeedback>
                </CCol>
              </CRow>
            </CForm>
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBack}
              >
                Cancel
              </CButton>
              <CButton color="primary" onClick={handleIdentityNext}>
                Next: Review
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
    {
      label: "Review",
      content: (
        <CCard>
          <CCardHeader>
            <h4 className="mb-0">Review</h4>
          </CCardHeader>
          <CCardBody>
            <p className="text-body-secondary">
              Check the details below, then save your changes.
            </p>
            <CListGroup className="mb-4">
              <CListGroupItem className="d-flex justify-content-between">
                <strong>First name</strong>
                <span>{form.firstName.trim() || "—"}</span>
              </CListGroupItem>
              <CListGroupItem className="d-flex justify-content-between">
                <strong>Last name</strong>
                <span>{form.lastName.trim() || "—"}</span>
              </CListGroupItem>
            </CListGroup>
            <div className="d-flex justify-content-between">
              <CButton
                color="secondary"
                variant="outline"
                onClick={handleBack}
                disabled={submitting}
              >
                Back
              </CButton>
              <CButton
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting && <CSpinner size="sm" className="me-2" />}
                Save changes
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      ),
    },
  ];

  return (
    <>
      <h2>Edit profile</h2>
      <p className="text-body-secondary">
        Update the name we use on your subscriptions.
      </p>
      <hr />

      <CStepper
        ref={stepperRef}
        steps={steps}
        onStepChange={setCurrentStep}
        onReset={() => setCurrentStep(1)}
      />
    </>
  );
}
